<script lang="ts">
    // @ts-nocheck
    import tabIcon from "@/assets/recept.svg";
    import BaseTab from "@/tabs/mixins/BaseTab.js";

    export default {
        name: "ReceptforskrivningStart",
        tabName: "Nationella läkemedelslistan",
        extends: BaseTab,
        patientBound: true,
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    // @ts-nocheck
    import { getConfig, klinikenApi } from "@/api";
    import { convertLakemedelData } from "@/store/modules/lakemedel/helpers";
    import { NllLock, NllLockSubject, NllLockType, SparrValue } from "@/store/modules/recept/helpers";
    import { SessionData } from "@/store/modules/recept/nllModule";
    import HavSparr from "@/tabs/Receptforskrivning/HavSparr.vue";
    import ReceptforskrivningsmallarButton from "@/tabs/Receptforskrivning/Mall/ReceptforskrivningsmallarButton.vue";
    import SambiLogin from "@/tabs/Receptforskrivning/SambiLogin.vue";
    import SparrForskrivning from "@/tabs/Receptforskrivning/SparrForskrivning.vue";
    import ReceptforskrivningList from "@/tabs/Receptforskrivning/Start/List/ReceptforskrivningList.vue";
    import { flashMessage } from "@/utils";
    import { getAge } from "@/utils/patient/patientData";
    import { toRefs, onBeforeMount, onMounted, onUnmounted, ref, computed, watch, set } from "vue";
    import { useStore } from "@/store";
    import KButton from "@/components/Button.vue";
    import KButtonGroup from "@/components/ButtonGroup.vue";
    import ModalContent from "@/components/generic/ModalContent.vue";
    import Receptforskrivning from "@/tabs/Receptforskrivning/Forskrivning/Receptforskrivning.vue";
    import Patientinfo from "@/tabs/Receptforskrivning/Patientinfo.vue";
    import KTag from "@/components/KTag.vue";
    import LaggTillSamtycke from "@/tabs/Receptforskrivning/Start/LaggTillSamtycke.vue";
    import SkickaSamtyckesforfragan from "@/tabs/Receptforskrivning/Start/SkickaSamtyckesforfragan.vue";
    import {
        NllSamtycke,
        ReceptforskrivningDisplay,
        isNLLPatientInfo,
        NLLPatientInfo,
        BasePatientInfo,
        hasRegisteredConsent,
        Sparr,
    } from "@/tabs/Receptforskrivning/types";
    import InformationNLL from "@/tabs/Receptforskrivning/Start/InformationNLL.vue";
    import PlusFilledIcon from "@/assets/plus_circle_filled_blue.svg";
    import PlusWhiteIcon from "@/assets/plus_circle_white.svg";
    import CheckIcon from "@/assets/check-dark.svg";
    import { fetchForskrivningar, getStructuredForskrivningar } from "@/tabs/Receptforskrivning/Start/helpers";
    import {
        hasPrescriptionRight,
        hasNLLReadPermission,
        convertHandelsvaraData,
        openNLLErrorDialog,
        sendSparr,
        inactivateSparr,
    } from "@/tabs/Receptforskrivning/helpers";
    import emitter from "tiny-emitter/instance";
    import { v4 as uuid4 } from "uuid";
    import cloneDeep from "lodash/cloneDeep";
    import moment from "moment";

    const store = useStore();

    const props = defineProps<{
        tabId: number;
    }>();

    const { tabId } = toRefs(props);

    const tableAktivaRef = ref();
    const tableHistoriskaRef = ref();

    const showInformationNLL = ref(false);
    const showLaggTillSamtycke = ref(false);
    const showSkickaSamtyckesforfragan = ref(false);

    defineExpose({
        tabId,
    });

    const isLoading = ref(false);
    const tokenChecked = ref(false);

    const forskrivningar = ref<{
        [key: string]: Array<ReceptforskrivningDisplay>;
    }>({});

    const showSparrDialog = ref(false);
    const havSparrType = ref<"SEKRETESS_VH">("SEKRETESS_VH");
    const havSparrId = ref<Sparr["id"] | null>(null);

    const profile = computed(() => store.getters["userData/currentProfile"]);
    // loginMode.component är satt när den här vyn auto-lanseras vid inloggning (se loginMode.js::launchView) utan
    // att användaren klickat i appen. Webbläsare kräver en riktig klickning ("user activation") för att tillåta
    // window.open(), så SambiLogin får inte auto-trigga sin popup i det läget - se dess autorun-prop nedan.
    const loginModeComponent = computed(() => store.getters["loginMode/component"]);
    const patientinfo = computed<NLLPatientInfo>(() => {
        return store.getters["nll/patientinfo"];
    });
    const dateOfBirth = computed(() => store.getters["patientData/dateOfBirth"]);
    const baseInfoHasBirthDate = computed(() => {
        const storedPatientinfo = store.getters["nll/patientinfo"];

        return isNLLPatientInfo(storedPatientinfo ?? {}) || (storedPatientinfo as BasePatientInfo)?.patient.dateOfBirth;
    });
    const sortedForskrivningar = computed<{
        aktiva: Array<ReceptforskrivningDisplay>;
        historiska: Array<ReceptforskrivningDisplay>;
        papper: Array<ReceptforskrivningDisplay>;
    }>(() =>
        getStructuredForskrivningar({
            forskrivningar: forskrivningar.value,
            localAccess: store.getters["nll/sessionData"]?.localAccess || !store.getters["nll/patientinfo"]?.samtycke,
        })
    );
    const sessionData = computed<SessionData>(() => store.getters["nll/sessionData"]);

    const openNyForskrivning = async () => {
        store.dispatch("tabs/openTab", {
            component: Receptforskrivning,
        });
    };
    const fetchReceptforskrivningar = async () => {
        try {
            isLoading.value = true;

            const { lakemedel: retrievedLakemedel, forskrivningar: structuredForskrivningar } =
                await fetchForskrivningar({
                    localAccess:
                        store.getters["nll/sessionData"]?.localAccess || !store.getters["nll/patientinfo"]?.samtycke,
                    patientinfo: store.getters["nll/patientinfo"],
                    headers: store.getters["nll/headers"],
                });

            forskrivningar.value = structuredForskrivningar;
            store.commit("lakemedel/addLakemedel", { lakemedel: retrievedLakemedel, offset: 0 });
        } catch (error) {
            openNLLErrorDialog({ error, description: "Kunde inte hämta förskrivningar." });
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Hämtar händelser och uttag för en förskrivning och stoppar in de samlingarna i förskrivningen i listan
     */
    const fetchForskrivningInfo = async ({ data }: { data: ReceptforskrivningDisplay }) => {
        try {
            const forskrivningIndex = forskrivningar.value?.[data.forskrivningskedja_id ?? "none"]?.findIndex(
                (forskrivning) => forskrivning.id === data.id
            );

            if (forskrivningIndex < 0) {
                throw new Error("Hittade inte i listan");
            }

            const forskrivning = forskrivningar.value[data.forskrivningskedja_id ?? "none"][forskrivningIndex];

            // Händelser och uttag hämtas i samma anrop
            if (forskrivning?.handelser) {
                return;
            }

            const baseinfoUrl = `nll/forskrivningar/${store.getters["patientData/currentId"]}/info/${
                (data as ReceptforskrivningDisplay).id
            }/`;
            const infoUrl = data.uttag_pappersrecept ? `${baseinfoUrl}?pappersrecept=true` : baseinfoUrl;

            const response = await klinikenApi.get<{
                handelser: ReceptforskrivningDisplay["handelser"];
                uttag: ReceptforskrivningDisplay["uttag"];
            }>(
                infoUrl,
                getConfig({
                    headers: store.getters["nll/headers"],
                })
            );

            const handelser = response.data.handelser;
            const uttag = response.data.uttag;

            handelser.sort((a, b) => {
                const timeA = moment.parseZone(a.tidpunkt);
                const timeB = moment.parseZone(b.tidpunkt);

                if (timeA.isSame(timeB)) {
                    return 0;
                }

                return timeA.isAfter(timeB) ? -1 : 1;
            });

            uttag.sort((a, b) => {
                const timeA = moment.parseZone(a.expeditionstidpunkt);
                const timeB = moment.parseZone(b.expeditionstidpunkt);

                if (timeA.isSame(timeB)) {
                    return 0;
                }

                return timeA.isAfter(timeB) ? -1 : 1;
            });

            set(forskrivningar.value[data.forskrivningskedja_id ?? "none"], forskrivningIndex, {
                ...cloneDeep(forskrivning),
                handelser: handelser.map((handelse) => {
                    const modifiedHandelse = handelse;

                    // Händelser för spärr saknar "spärr" i label
                    if (["REGISTERED", "UNREGISTERED"].includes(handelse.handelse_typ)) {
                        modifiedHandelse.handelse = `${modifiedHandelse.handelse} spärr`;
                    }

                    return modifiedHandelse;
                }),
                uttag: uttag.map(({ lakemedel, handelsvara, ...uttag }) => {
                    if (lakemedel) {
                        const lakemedelDisplay = convertLakemedelData({ lakemedel });

                        return {
                            ...uttag,
                            lakemedelInfo: lakemedelDisplay?.lakemedel,
                        };
                    }

                    const handelsvaraDisplay = convertHandelsvaraData({ handelsvara });

                    return {
                        ...uttag,
                        lakemedelInfo: handelsvaraDisplay?.lakemedelInfo,
                    };
                }),
            });
        } catch (error: any) {
            console.error(error);

            openNLLErrorDialog({
                error,
                description: "Kunde inte hämta information händelser och uttag för förskrivningen.",
            });
        }
    };

    const toggleSparrDialog = () => (showSparrDialog.value = !showSparrDialog.value);

    const toggleHavSparrDialog = () => {
        if (havSparrId.value) {
            havSparrId.value = null;
        } else if (patientinfo?.value?.sparr_pappersrecept_vardnadshavare) {
            havSparrId.value = patientinfo.value.sparr_pappersrecept_vardnadshavare;
        }
    };

    onBeforeMount(async () => {
        store.dispatch("nll/addSubscriber");
        store.commit("nll/setContextId", { contextId: uuid4() });

        /* Om det saknas accessToken så börja med att testa om backend har en sparad */
        if (!sessionData?.value?.accessToken) {
            try {
                const response = await klinikenApi.get("/nll/sambi/token", getConfig());
                if (response && response.status === 200) {
                    await store.dispatch("auth/addSambi", {
                        data: response.data,
                    });
                }
            } catch (error: any) {
                // 204 (TOKEN_NOT_FOUND) är ett förväntat fall – fortsätt utan sparad token
                if (error?.response?.status === 204) {
                    // Ignorera, vi fortsätter utan sparad token
                } else {
                    // Logga andra fel men låt initiering fortsätta
                    // t.ex. 401 eller nätverksfel
                    console.error("Misslyckades att hämta sparad NLL Sambi-token", error);
                }
            }
        }

        tokenChecked.value = true;

        /* Om det nu finns en accessToken så använd den */
        if (sessionData?.value?.accessToken) {
            await store.dispatch("nll/fetchPatientinfo");
        } else {
            await store.dispatch("nll/loadInternalPatientinfo");
        }
    });

    const onSubmitSparr = async (data: SparrValue) => {
        /*
         TODO Duplicerad kod i formuläret och listan. Anropet skulle kunna flyttas in där och prop som skickas in kan
         avgöra om den ska anropas eller inte
         */
        await sendSparr({
            patient_id: store.getters["patientData/patient"]?.id,
            sparr: data,
            headers: store.getters["nll/headers"],
        });

        toggleSparrDialog();
        flashMessage("Spärr skapad.");
        emitter.emit("refreshReceptforskrivningar");
    };

    const onHavSparrSubmit = async (sparrId: string) => {
        try {
            await inactivateSparr({
                patient_id: store.getters["patientData/patient"]?.id,
                sparr_id: sparrId,
                headers: store.getters["nll/headers"],
            });

            toggleHavSparrDialog();

            flashMessage("Spärren hävd.");
            emitter.emit("refreshReceptforskrivningar");
        } catch (error) {
            console.error(error);
        }
    };

    const onHavSparrCancel = async () => {
        toggleHavSparrDialog();
    };

    onMounted(async () => {
        /**
         * Emittas från Receptforskrivning.vue när den tabben stängs
         */
        emitter.on("refreshReceptforskrivningar", async () => {
            // Context id kan ha rensats när token från Sambi gått ut
            if (!sessionData?.value?.contextId) {
                store.commit("nll/setContextId", { contextId: uuid4() });
            }

            await store.dispatch("nll/fetchPatientinfo");
            await fetchReceptforskrivningar();

            /*
                Gör en ny hämtning av händelser och uttag för expanderade rader, då anropet ovan skriver över
                existerande data och gör att händelser och uttag blir tomma
             */

            const expandedAktiva = tableAktivaRef?.value?.getExpandedRows?.() ?? [];
            const expandedHistoriska = tableHistoriskaRef?.value?.getExpandedRows?.() ?? [];

            if (expandedAktiva.length) {
                await fetchForskrivningInfo({ data: expandedAktiva[0] });
            }

            if (expandedHistoriska.length) {
                await fetchForskrivningInfo({ data: expandedHistoriska[0] });
            }
        });

        /**
         * Rensar lokal data och hämtar data från BE som är tillgänglig när användaren inte är inloggad.
         * accessToken är tomt om t.ex refresh token inte längre är giltig
         */
        watch(sessionData, async (newSessionData) => {
            if (newSessionData && !newSessionData?.accessToken) {
                store.commit("nll/resetSessionData");
                store.commit("nll/resetPatientinfo");
                forskrivningar.value = {};

                await store.dispatch("nll/loadInternalPatientinfo");
                await fetchReceptforskrivningar();
            }
        });
    });

    onUnmounted(() => {
        store.dispatch("nll/removeSubscriber");
        emitter.off("refreshReceptforskrivningar");
    });

    watch(patientinfo, async (newPatientinfo) => {
        if (newPatientinfo?.loading) {
            store.dispatch("tabs/displaySpinner", {
                id: tabId.value,
                display: true,
                loadingMessage: "Hämtar personuppgifter från NLL",
            });
        } else if (newPatientinfo) {
            forskrivningar.value = {};
            store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
            await fetchReceptforskrivningar();
        }
    });
</script>

<template>
    <div class="receptforskrivning-start">
        <div class="header-section">
            <div class="header">
                <h2>Nationella läkemedelslistan</h2>
                <ReceptforskrivningsmallarButton />
            </div>
            <div v-if="patientinfo" class="info-section">
                <h3 aria-hidden="false">Patientinfo</h3>
                <Patientinfo :patientinfo="patientinfo" />
                <div class="button-group">
                    <template
                        v-if="
                            isNLLPatientInfo(patientinfo) &&
                            hasNLLReadPermission(profile) &&
                            (!dateOfBirth ||
                                getAge({
                                    dateOfBirth,
                                }) >= 18)
                        "
                    >
                        <KButton
                            v-if="!hasRegisteredConsent(patientinfo.samtycke) && !patientinfo.samtyckesforfragan_finns"
                            type="button"
                            size="small"
                            variant="contour"
                            @click="showSkickaSamtyckesforfragan = true"
                            aria-label="Info NLL"
                        >
                            Förfrågan om registrerat åtkomstsamtycke
                        </KButton>
                        <div
                            v-else-if="patientinfo.samtyckesforfragan_finns === 'proposed'"
                            class="samtyckesforfragan-skickad"
                        >
                            <img :src="CheckIcon" class="check-icon" />
                            <span>Förfrågan om registrerat åtkomstsamtycke skickad</span>
                        </div>
                    </template>
                    <KButton
                        type="button"
                        size="small"
                        variant="contour"
                        @click="showInformationNLL = true"
                        aria-label="Info NLL"
                    >
                        Info NLL
                    </KButton>
                </div>
            </div>
            <template
                v-if="
                    patientinfo &&
                    dateOfBirth &&
                    getAge({
                        dateOfBirth,
                    }) < 18 &&
                    patientinfo.samtycke
                "
            >
                <KButton
                    v-if="!patientinfo.sparr_pappersrecept_vardnadshavare"
                    type="button"
                    variant="contour-alert"
                    size="slim"
                    class="sparr-papper"
                    @click="toggleSparrDialog"
                >
                    Spärra uttag pappersrecept
                </KButton>
                <KButton
                    v-else
                    type="button"
                    variant="contour-alert"
                    size="slim"
                    class="sparr-papper"
                    @click="toggleHavSparrDialog"
                >
                    Häv spärr på uttag pappersrecept
                </KButton>
            </template>
        </div>
        <ModalContent
            title="Information NLL"
            v-if="showInformationNLL"
            @close="showInformationNLL = false"
            showHeader
            showCloseButton
        >
            <InformationNLL @close="showInformationNLL = false" />
        </ModalContent>
        <div v-if="sessionData && !sessionData?.accessConsent" class="consent-prescription-section">
            <template v-if="hasNLLReadPermission(profile) && sessionData?.accessToken">
                <template v-if="isNLLPatientInfo(patientinfo)">
                    <h3 aria-hidden="false">Samtycke</h3>
                    <div class="consent-section">
                        <h4>Samtycke för patienten saknas</h4>
                        <KTag variant="warning" size="large">
                            <span>
                                Du kan inte få åtkomst till patientens läkemedelslista utan samtycke eller annat skäl
                                till åtkomst. Välj åtkomsttyp för att gå vidare.
                            </span>
                        </KTag>
                        <KButtonGroup>
                            <KButton
                                type="button"
                                @click="showLaggTillSamtycke = true"
                                size="secondary"
                                aria-label="Val av åtkomsttyp"
                            >
                                Val av åtkomsttyp
                            </KButton>
                            <KButton
                                v-if="
                                    (!dateOfBirth ||
                                        getAge({
                                            dateOfBirth,
                                        }) >= 18) &&
                                    !hasRegisteredConsent(patientinfo.samtycke) &&
                                    !patientinfo.samtyckesforfragan_finns
                                "
                                size="secondary"
                                @click="showSkickaSamtyckesforfragan = true"
                            >
                                Förfrågan om registrerat åtkomstsamtycke
                            </KButton>
                        </KButtonGroup>
                    </div>
                    <div class="divider"></div>
                </template>
                <div v-if="patientinfo" class="prescription-section-no-consent">
                    <h4>Skapa förskrivning utan samtycke att se läkemedelslista</h4>
                    <KTag v-if="!baseInfoHasBirthDate" variant="warning" size="large">
                        <span>
                            Patientens födelsedatum saknas. Uppdatera patientens information för att kunna skapa en
                            förskrivning.
                        </span>
                    </KTag>
                    <KTag v-else-if="!patientinfo?.samtycke" variant="info" size="large">
                        <span>Du kan skapa en förskrivning utan att hämta patientens läkemedelslista från NLL</span>
                    </KTag>
                    <KButtonGroup>
                        <KButton
                            type="button"
                            @click="openNyForskrivning"
                            size="secondary"
                            variant="contour"
                            aria-label="Ny förskrivning"
                            :disabled="!hasPrescriptionRight(profile) || !baseInfoHasBirthDate"
                        >
                            <img :src="PlusFilledIcon" alt="Ny förskrivning" />
                            <span>Ny förskrivning</span>
                        </KButton>
                    </KButtonGroup>
                </div>
            </template>
            <template v-else>
                <div class="sambi-login">
                    <KTag variant="warning" size="large">
                        <span v-if="hasNLLReadPermission(profile)"
                            >Du är inte inloggad i Sambi och har inte åtkomst till NLL</span
                        >
                        <span v-else>Du har inte behörighet att hämta förskrivningar från NLL</span>
                    </KTag>
                    <SambiLogin
                        v-if="hasNLLReadPermission(profile) && tokenChecked && !sessionData?.accessToken"
                        :autorun="!loginModeComponent"
                    />
                </div>
            </template>
        </div>
        <div v-else-if="patientinfo" class="create-section">
            <h3 aria-hidden="false">Ny förskrivning eller nödåtkomst</h3>
            <KButton
                class="main-ny-forskrivning"
                type="button"
                @click="openNyForskrivning"
                size="secondary"
                aria-label="Ny förskrivning"
                :disabled="!hasPrescriptionRight(profile) || !baseInfoHasBirthDate"
            >
                <img :src="PlusWhiteIcon" alt="Ny förskrivning" />
                <span>Ny förskrivning</span>
            </KButton>
            <!-- Användare ska kunna be om nödåtkomst som samtycke även om ett samtycke redan existerar -->
            <KButton
                v-if="
                    hasNLLReadPermission(profile) &&
                    isNLLPatientInfo(patientinfo) &&
                    sessionData?.accessToken &&
                    sessionData?.accessConsent &&
                    !patientinfo.dospatient &&
                    sessionData.accessConsent !== NllSamtycke.TillfalligtSparradeUppgifter &&
                    sessionData.accessConsent !== NllSamtycke.Nodatkomst &&
                    sessionData.accessConsent !== NllSamtycke.UtanKravBarn
                "
                type="button"
                @click="showLaggTillSamtycke = true"
                size="secondary"
                aria-label="Nödåtkomst"
            >
                Nödåtkomst
            </KButton>
        </div>

        <div v-if="patientinfo" class="forskrivningar">
            <h3 aria-hidden="false">Förskrivningar</h3>
            <template
                v-if="
                    isNLLPatientInfo(patientinfo) &&
                    sessionData.accessConsent &&
                    sessionData.accessConsent !== NllSamtycke.UtanKrav
                "
            >
                <ReceptforskrivningList
                    ref="tableAktivaRef"
                    title="Aktiva förskrivningar"
                    loadingMessage="Hämtar patientens läkemedelslista från NLL"
                    :loading="isLoading"
                    :forskrivningar="sortedForskrivningar?.aktiva ?? []"
                    @rowClick="fetchForskrivningInfo"
                    type="aktiva"
                />
                <ReceptforskrivningList
                    v-if="sortedForskrivningar?.papper?.length"
                    ref="tablePapperRef"
                    title="Uttag pappersrecept"
                    loadingMessage="Hämtar patientens läkemedelslista från NLL"
                    :loading="isLoading"
                    :forskrivningar="sortedForskrivningar?.papper"
                    @rowClick="fetchForskrivningInfo"
                    type="papper"
                />
                <ReceptforskrivningList
                    ref="tableHistoriskaRef"
                    title="Historiska förskrivningar"
                    loadingMessage="Hämtar patientens läkemedelslista från NLL"
                    :loading="isLoading"
                    :forskrivningar="sortedForskrivningar?.historiska ?? []"
                    @rowClick="fetchForskrivningInfo"
                    type="historiska"
                />
            </template>
            <template v-else>
                <ReceptforskrivningList
                    title="Lokala förskrivningar"
                    loadingMessage="Hämtar patientens läkemedelslista från Kliniken"
                    :loading="isLoading"
                    :forskrivningar="[
                        ...(sortedForskrivningar?.aktiva ?? []),
                        ...(sortedForskrivningar?.historiska ?? []),
                    ]"
                    type="lokala"
                >
                    <template #tags>
                        <KTag variant="info" size="large">
                            <span>
                                Observera att det kan finnas senare ändringar och förskrivningar i NLL. Här kan du
                                endast se lokala förskrivningar som skickats från din vårdenhet.
                            </span>
                        </KTag>
                    </template>
                </ReceptforskrivningList>
            </template>
        </div>
        <ModalContent
            title="Registrerat åtkomstsamtycke"
            v-if="showSkickaSamtyckesforfragan"
            @close="showSkickaSamtyckesforfragan = false"
            showHeader
            showCloseButton
        >
            <SkickaSamtyckesforfragan @close="showSkickaSamtyckesforfragan = false" />
        </ModalContent>
        <ModalContent
            title="Val av åtkomsttyp"
            v-if="showLaggTillSamtycke"
            @close="showLaggTillSamtycke = false"
            showHeader
            showCloseButton
        >
            <LaggTillSamtycke @close="showLaggTillSamtycke = false" @submit="showLaggTillSamtycke = false" />
        </ModalContent>
        <portal v-if="showSparrDialog" to="modal">
            <SparrForskrivning
                @submit="onSubmitSparr"
                @cancel="toggleSparrDialog"
                :receptforskrivning="null"
                :overrideOptions="[
                    NllLock[`${NllLockType.SEKRETESS_VH.code}-${NllLockSubject.UTTAGPAPPERSRECEPT.code}`],
                ]"
                :overrideValue="{
                    sparr_legal_ground: NllLockType.SEKRETESS_VH.code,
                    sparr_subject_type: NllLockSubject.UTTAGPAPPERSRECEPT.code,
                }"
                :clearable="false"
            />
        </portal>
        <HavSparr
            :sparrId="havSparrId"
            :sparrType="havSparrType"
            :isPapper="true"
            @submit="onHavSparrSubmit"
            @cancel="onHavSparrCancel"
        />
    </div>
</template>

<style lang="scss">
    @import "@/style/variables";
    @import "@/style/container";

    .receptforskrivning-start {
        display: flex;
        flex-direction: column;
        width: $tab-max-width;
        max-width: 100%;
        gap: 1.2rem;

        .info-section {
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            row-gap: 0.5rem;

            .button-group {
                display: flex;
                gap: 1rem;
                align-items: center;
                margin-left: auto;

                .check-icon {
                    margin-right: 0.5rem;
                }
            }
        }

        .consent-prescription-section {
            display: flex;
            border-bottom: 1px solid $list-border-color;
            margin-top: 1rem;
            padding-bottom: 1rem;
            margin-bottom: 2rem;

            .consent-section {
                width: 100%;
                flex-grow: 1;
            }

            .divider {
                width: 0;
                border-left: 1px solid $list-border-color;
                margin: 6px 0;
                margin: 0 26px;
            }

            .prescription-section-no-consent {
                width: 100%;
                flex-grow: 1;
            }

            .sambi-login {
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }
        }

        .create-section {
            display: grid;
            align-items: center;
            grid-auto-flow: column;
            grid-auto-columns: max-content;
            grid-gap: 1rem;
        }

        .forskrivningar {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }

        .header-section {
            .header {
                display: flex;
                gap: 1.5rem;
            }
            .sparr-papper {
                margin-top: 1rem;
            }
        }
    }
</style>
